import { KbjCard, KbjCardMeta } from '@eika-kortbetjening/types';

import { isOrderedToNets } from '@/components/card/card-info-with-functionality/functions';
import { Format } from '@/domain/app';
import { KortbetjeningText } from '@/hooks/use-text';
import { getBankCardName, isExpired } from '@/utils/card-utils';

import { Card, CardBlockCode, CardStatus, DebitAccount, Market, OrderCardStatus, Session } from '@generated-types';
import { capitalizeFirsts } from '@utils/string';

import { CardService } from '.';

const toKbjPanel = (
    card: Card,
    session: Session,
    cardName: string,
    getUrl: (card: Card) => string,
): [KbjCard, KbjCardMeta] => {
    return [
        {
            embossedName: card.embossedName,
            number: Format.cardNumber(card.number),
            bankRegistrationNumber: card.product.code,
            orderStatus: card.orderStatus,
            status: card.status,
            name: cardName,
            url: getUrl(card),
            imageUrl: card.imagePath,
        },
        {
            state: {
                isActive: card.status === CardStatus.OK,
                isBlocked: !!card.block,
                isExpired: isExpired(card),
                isOrdered: card.orderStatus === OrderCardStatus.ORDERED,
                isOrderedToNets: isOrderedToNets(card),
                isOwnedByUser: CardService.isOwnerOfCard(card, session),
                isReplaced: false,
                isSoftblocked: card.block?.code === CardBlockCode.SOFT_BLOCK,
            },
            market: card.market === Market.PRIVATE ? 'private' : 'business',
        },
    ];
};

const toKbjDebitPanel = (
    card: Card,
    session: Session,
    getUrl: (card: Card) => string,
    text: KortbetjeningText,
    account?: DebitAccount,
): [KbjCard, KbjCardMeta] => {
    const url = getUrl(card);

    return [
        {
            url,
            name: getBankCardName(card, text, account),
            number: Format.cardNumber(card.number),
            bankRegistrationNumber: card.bankRegistrationNumber,
            imageUrl: card.imagePath,
            embossedName: card.embossedName.toString(),
            orderStatus: card.orderStatus,
            status: card.status,
            block: card.block?.code,
            created: card.created,
        },
        {
            market: card.market === Market.PRIVATE ? 'private' : 'business',
            state: {
                isActive: card.status === CardStatus.OK,
                isBlocked: !!card.block,
                isExpired: isExpired(card),
                isOrdered: card.orderStatus === OrderCardStatus.ORDERED,
                isOrderedToNets: isOrderedToNets(card),
                isOwnedByUser: CardService.isOwnerOfCard(card, session),
                isReplaced: false,
                isSoftblocked: false,
            },
            otherOwnerName: session.user?.userId !== card.owner ? capitalizeFirsts(card.embossedName) : undefined,
        },
    ];
};

export const KbjCardService = {
    toKbjPanel,
    toKbjDebitPanel,
};
